import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import BlockIcon from '@mui/icons-material/Block';
import HistoryIcon from '@mui/icons-material/History';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import AddIcon from '@mui/icons-material/Add';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useDispatch, useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { setActivityDrawer } from '@store/appStore';
import FilterBar from '@components/Utilities/FilterBar';
import ExpenseGroupChip from '@components/Expenses/ExpenseGroupChip';
import { CashFlow, ExpenseGroup } from '@api/CashFlows/CashFlow';
import { EXPENSE_GROUPS, ExpenseCategory } from '@api/ExpenseCategories/ExpenseCategory';
import ExpenseCategoryServ from '@api/ExpenseCategories/ExpenseCategoryServ';
import { buildActivityOrigin } from '@api/ActivityLogs/ActivityOrigin';
import { EXPENSE_ACTIVITY_SECTION } from '@api/ActivityLogs/ExpenseActivity';
import { currencyFormatter } from '@utils/Numbers';
import { toDateSafe } from '@utils/Date';
import { canCurrentUser, getCurrentPermissionSubject } from '@utils/Permissions';
import { Capability, getExpenseListScope } from '@utils/Permissions/Capabilities';
import { expenseCategoryKeyOf, expenseGroupOf, SHIPPING_CATEGORY_ID, UNCATEGORIZED } from '@utils/CashFlows';
import {
    EXPENSE_STATUS_LABELS,
    ExpenseListFilters,
    expenseStatusOf,
    filterExpenses,
    formatPeriod,
    summarizeExpenses
} from '@utils/Expenses';
import useUserNames from '@hooks/useUserNames';
import ModalCancelExpense from '@components/SalesReport/Expenses/ModalCancelExpense';
import ModalExpenseForm from '@components/SalesReport/Expenses/ModalExpenseForm';
import ModalConfirmExpense from '@components/SalesReport/Expenses/ModalConfirmExpense';

interface ExpensesListProps {
    /** Los `cash-flows` que ya trajo el Informe para su periodo y sucursal: cero lecturas extra. */
    cashFlows: CashFlow[];
    /** Sucursal del filtro del Informe (`'all'` = todas las visibles). */
    branchOfficeId: string;
    filters: ExpenseListFilters;
    onFiltersChange: (filters: ExpenseListFilters) => void;
    /** Algo cambió (registrar, editar, cancelar, confirmar): el Informe vuelve a leer. */
    onChanged: () => void;
}

const STATUS_OPTIONS = (Object.keys(EXPENSE_STATUS_LABELS) as (keyof typeof EXPENSE_STATUS_LABELS)[]).map((status) => ({
    value: status,
    label: EXPENSE_STATUS_LABELS[status]
}));

/** Los KPIs de la vista 5: total y los renglones que más pesan. */
const KPI_GROUPS: { label: string; groups: string[] }[] = [
    { label: 'Insumos', groups: [ExpenseGroup.Supplies] },
    { label: 'Nómina', groups: [ExpenseGroup.Payroll] },
    { label: 'Fijos y variables', groups: [ExpenseGroup.Fixed, ExpenseGroup.Variable] },
    { label: 'Operativo y mantenimiento', groups: [ExpenseGroup.Operating, ExpenseGroup.Maintenance] }
];

const Kpi: React.FunctionComponent<{ label: string; value: number; caption?: string; strong?: boolean }> = ({
    label,
    value,
    caption,
    strong
}) => (
    <Stack minWidth={150}>
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
        <Typography fontSize={strong ? 22 : 18} fontWeight={700}>
            {currencyFormatter(value)}
        </Typography>
        {caption && (
            <Typography variant="caption" color="text.secondary">
                {caption}
            </Typography>
        )}
    </Stack>
);

/**
 * Lista de gastos de la pestaña Gastos (plan de Gastos §5.2, vistas 5 y 6). Lee lo
 * mismo que el Informe (la consulta de siempre: `date` en rango + sucursal, ~14
 * gastos al día en las 10 sucursales ≈ 430 docs por un mes consolidado) y filtra en
 * el cliente. Los cancelados se ven tachados y no suman.
 */
const ExpensesList: React.FunctionComponent<ExpensesListProps> = ({
    cashFlows,
    branchOfficeId,
    filters,
    onFiltersChange,
    onChanged
}) => {
    const dispatch = useDispatch();
    const { branchOffices } = useSelector((state: RootReducer) => state.krispis);
    const { loggedUser } = useSelector((state: RootReducer) => state.auth);
    const [categories, setCategories] = useState<ExpenseCategory[]>([]);
    const [canceling, setCanceling] = useState<CashFlow | undefined>(undefined);
    const [editing, setEditing] = useState<CashFlow | undefined>(undefined);
    const [confirming, setConfirming] = useState<CashFlow | undefined>(undefined);
    const [creating, setCreating] = useState(false);

    const subject = getCurrentPermissionSubject();
    const scope = getExpenseListScope(subject);

    useEffect(() => {
        ExpenseCategoryServ.getExpenseCategories()
            .then(setCategories)
            .catch(() => setCategories([]));
    }, []);

    const visible = useMemo(
        () =>
            scope
                ? filterExpenses(cashFlows, filters, {
                      onlyOwn: scope.onlyOwn,
                      onlyToday: scope.onlyToday,
                      uid: loggedUser?.uid
                  })
                : [],
        [cashFlows, filters, scope?.onlyOwn, scope?.onlyToday, loggedUser?.uid]
    );
    // Las opciones de "Capturó" salen de TODOS los gastos del periodo, no de los filtrados.
    const allExpenses = useMemo(
        () =>
            scope
                ? filterExpenses(
                      cashFlows,
                      { categoryKey: 'all', group: 'all', userId: 'all', status: 'all' },
                      { onlyOwn: scope.onlyOwn, onlyToday: scope.onlyToday, uid: loggedUser?.uid }
                  )
                : [],
        [cashFlows, scope?.onlyOwn, scope?.onlyToday, loggedUser?.uid]
    );
    const names = useUserNames(allExpenses.map((flow) => flow.userId));
    const summary = useMemo(() => summarizeExpenses(visible), [visible]);
    const branchName = (id?: string) => branchOffices.find((branch) => branch.id === id)?.name || '—';
    const showBranch = branchOfficeId === 'all';

    if (!scope) {
        return (
            <Box p={3}>
                <Typography color="text.secondary">Tu perfil no ve la lista de gastos.</Typography>
            </Box>
        );
    }

    const categoryOptions = [
        ...categories.map((category) => ({ value: category.id, label: category.name })),
        { value: UNCATEGORIZED, label: 'Sin categoría (antes de categorizar)' }
    ].filter((option, index, list) => list.findIndex((o) => o.value === option.value) === index);
    const groupOptions = [
        ...EXPENSE_GROUPS.map((info) => ({ value: info.group as string, label: info.name })),
        { value: UNCATEGORIZED, label: 'Sin categoría' }
    ];
    const userOptions = Object.entries(
        allExpenses.reduce<Record<string, string>>((acc, flow) => {
            if (flow.userId) acc[flow.userId] = names[flow.userId] || 'Sin nombre';
            return acc;
        }, {})
    )
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const set = (changes: Partial<ExpenseListFilters>) => onFiltersChange({ ...filters, ...changes });

    const openActivity = (flow: CashFlow) => {
        const origin = buildActivityOrigin(flow as unknown as Record<string, unknown>, {
            verb: flow.generatedBy ? 'generó el gasto fijo del mes' : 'registró el gasto',
            noun: 'Gasto registrado',
            name: flow.userId ? names[flow.userId] : undefined
        });
        dispatch(
            setActivityDrawer({
                isOpen: true,
                section: EXPENSE_ACTIVITY_SECTION,
                sectionId: flow.id,
                derived: origin ? [origin] : []
            })
        );
    };

    const categoryLabel = (flow: CashFlow): string => {
        const key = expenseCategoryKeyOf(flow);
        if (flow.categoryName) return flow.categoryName;
        if (key === SHIPPING_CATEGORY_ID) return 'Envío';
        return 'Sin categoría';
    };

    return (
        <Stack height="100%" minHeight={0} spacing={2} p={2} boxSizing="border-box">
            <FilterBar
                filters={[
                    { key: 'group', label: 'Tipo', value: filters.group, options: groupOptions, onChange: (group) => set({ group }) },
                    {
                        key: 'category',
                        label: 'Categoría',
                        value: filters.categoryKey,
                        options: categoryOptions,
                        onChange: (categoryKey) => set({ categoryKey })
                    },
                    { key: 'user', label: 'Capturó', value: filters.userId, options: userOptions, onChange: (userId) => set({ userId }) },
                    {
                        key: 'status',
                        label: 'Estado',
                        value: filters.status,
                        options: STATUS_OPTIONS,
                        onChange: (status) => set({ status: status as ExpenseListFilters['status'] })
                    }
                ]}
                onClear={() => onFiltersChange({ categoryKey: 'all', group: 'all', userId: 'all', status: 'all' })}
            >
                {canCurrentUser(Capability.ExpenseCaptureOperating) && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
                        Registrar gasto
                    </Button>
                )}
            </FilterBar>

            {scope.onlyToday && (
                <Typography variant="body2" color="text.secondary">
                    Ves los gastos que tú registraste hoy.
                </Typography>
            )}

            <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap flexShrink={0}>
                <Kpi
                    strong
                    label="Total de gastos"
                    value={summary.total}
                    caption={[
                        `${summary.count} gasto${summary.count === 1 ? '' : 's'}`,
                        summary.cancelledCount ? `${summary.cancelledCount} cancelado${summary.cancelledCount === 1 ? '' : 's'} (no suman)` : '',
                        summary.outsideStatement ? `${currencyFormatter(summary.outsideStatement)} no son gasto (propinas, ajustes)` : ''
                    ]
                        .filter(Boolean)
                        .join(' · ')}
                />
                {KPI_GROUPS.map((kpi) => (
                    <Kpi
                        key={kpi.label}
                        label={kpi.label}
                        value={kpi.groups.reduce((total, group) => total + (summary.byGroup[group] || 0), 0)}
                    />
                ))}
                <Kpi
                    label="Salió de la caja"
                    value={summary.fromCash}
                    caption={summary.estimated ? `${currencyFormatter(summary.estimated)} por confirmar` : undefined}
                />
            </Stack>

            <TableContainer sx={{ flex: 1, minHeight: 0 }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Fecha</TableCell>
                            {showBranch && <TableCell>Sucursal</TableCell>}
                            <TableCell>Categoría</TableCell>
                            <TableCell>Descripción</TableCell>
                            <TableCell>Capturó</TableCell>
                            <TableCell>Caja</TableCell>
                            <TableCell align="right">Monto</TableCell>
                            <TableCell>Estado</TableCell>
                            <TableCell align="right">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visible.map((flow) => {
                            const status = expenseStatusOf(flow);
                            const canceled = status === 'cancelled';
                            const date = toDateSafe(flow.date);
                            const canEdit =
                                status === 'active' &&
                                canCurrentUser(Capability.ExpenseEdit, { branchOfficeId: flow.branchOfficeId, date });
                            const canCancel =
                                !canceled && canCurrentUser(Capability.ExpenseCancel, { branchOfficeId: flow.branchOfficeId });
                            const canConfirm =
                                status === 'pending' &&
                                canCurrentUser(Capability.RecurringExpenseManage, { branchOfficeId: flow.branchOfficeId });
                            const struck = canceled ? { textDecoration: 'line-through', color: 'text.disabled' } : {};
                            return (
                                <TableRow key={flow.id} hover>
                                    <TableCell sx={{ whiteSpace: 'nowrap', ...struck }}>
                                        {date ? format(date, 'dd MMM yy, HH:mm', { locale: es }) : '—'}
                                    </TableCell>
                                    {showBranch && <TableCell sx={struck}>{branchName(flow.branchOfficeId)}</TableCell>}
                                    <TableCell sx={{ opacity: canceled ? 0.5 : 1 }}>
                                        <ExpenseGroupChip group={expenseGroupOf(flow)} label={categoryLabel(flow)} />
                                        {flow.period && (
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                {formatPeriod(flow.period)}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 320, ...struck }}>
                                        <Typography variant="body2" sx={struck}>
                                            {flow.description || '—'}
                                        </Typography>
                                        {/* Compra en la calle (plan de inventario F1): ¿ya la reflejó el ledger? */}
                                        {flow.purchaseQuantity !== undefined && !canceled && (
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                color={flow.inventorySyncPending ? 'warning' : 'success'}
                                                label={flow.inventorySyncPending ? 'Por entrar al inventario' : 'En inventario'}
                                                sx={{ mt: 0.5 }}
                                            />
                                        )}
                                        {canceled && flow.deleteReason && (
                                            <Typography variant="caption" color="error" display="block">
                                                {`Cancelado${flow.deletedByName ? ` por ${flow.deletedByName}` : ''}: ${flow.deleteReason}`}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={struck}>{(flow.userId && names[flow.userId]) || '—'}</TableCell>
                                    <TableCell sx={struck}>{flow.paidFromCash === false ? 'Otro dinero' : 'De la caja'}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap', ...struck }}>
                                        {currencyFormatter(flow.amount)}
                                    </TableCell>
                                    <TableCell>
                                        {status === 'active' ? (
                                            <Typography variant="body2">{EXPENSE_STATUS_LABELS.active}</Typography>
                                        ) : (
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                color={status === 'pending' ? 'warning' : 'default'}
                                                label={EXPENSE_STATUS_LABELS[status]}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        {canConfirm && (
                                            <Tooltip title="Confirmar el monto">
                                                <IconButton size="small" aria-label="Confirmar el monto" onClick={() => setConfirming(flow)}>
                                                    <TaskAltIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {canEdit && (
                                            <Tooltip title="Editar">
                                                <IconButton size="small" aria-label="Editar gasto" onClick={() => setEditing(flow)}>
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {canCancel && (
                                            <Tooltip title="Cancelar">
                                                <IconButton size="small" aria-label="Cancelar gasto" onClick={() => setCanceling(flow)}>
                                                    <BlockIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Actividad">
                                            <IconButton size="small" aria-label="Actividad del gasto" onClick={() => openActivity(flow)}>
                                                <HistoryIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {visible.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={showBranch ? 9 : 8}>
                                    <Typography color="text.secondary" py={2} textAlign="center">
                                        No hay gastos en este periodo con estos filtros.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <ModalCancelExpense expense={canceling} onClose={() => setCanceling(undefined)} onCanceled={onChanged} />
            <ModalExpenseForm
                open={creating || !!editing}
                editing={editing}
                defaultBranchOfficeId={branchOfficeId}
                onClose={() => {
                    setCreating(false);
                    setEditing(undefined);
                }}
                onSaved={onChanged}
            />
            <ModalConfirmExpense expense={confirming} onClose={() => setConfirming(undefined)} onConfirmed={onChanged} />
        </Stack>
    );
};

export default ExpensesList;
